import React from 'react';
import type { OrderStatus, ShippingStatus } from '../types';
import styles from './OrderStatus.module.css';

interface OrderStatusProps {
  orderStatus: OrderStatus;
  shippingStatus: ShippingStatus;
  orderItems?: Array<{
    name: string;
    quantity: number;
    size: string;
    price: number;
  }>;
}

const OrderStatus: React.FC<OrderStatusProps> = ({
  orderStatus,
  shippingStatus,
  orderItems,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
      case 'delivered':
        return styles.statusBadgePaid;
      case 'pending':
      case 'processing':
        return styles.statusBadgePending;
      case 'shipped':
        return styles.statusBadgeShipped;
      case 'cancelled':
      case 'failed':
      case 'returned':
        return styles.statusBadgeCancelled;
      default:
        return styles.statusBadgeDefault;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return '💰';
      case 'pending':
        return '⏳';
      case 'processing':
        return '⚙️';
      case 'shipped':
        return '📦';
      case 'delivered':
        return '✅';
      case 'cancelled':
        return '❌';
      case 'failed':
        return '💥';
      case 'returned':
        return '🔄';
      default:
        return '❓';
    }
  };

  return (
    <div className={styles.orderStatusContainer}>
      <h2 className={styles.orderStatusTitle}>Order Status</h2>

      <div className={styles.statusSection}>
        {/* Order Status */}
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>Order Status:</span>
          <span
            className={`${styles.statusBadge} ${getStatusColor(orderStatus)}`}
          >
            {getStatusIcon(orderStatus)}{' '}
            {orderStatus.charAt(0).toUpperCase() + orderStatus.slice(1)}
          </span>
        </div>

        {/* Shipping Status */}
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>Shipping:</span>
          <span
            className={`${styles.statusBadge} ${getStatusColor(
              shippingStatus
            )}`}
          >
            {getStatusIcon(shippingStatus)}{' '}
            {shippingStatus.charAt(0).toUpperCase() + shippingStatus.slice(1)}
          </span>
        </div>
      </div>

      {/* Order Items */}
      {orderItems && orderItems.length > 0 && (
        <div className={styles.orderItemsSection}>
          <h3 className={styles.orderItemsTitle}>Order Items:</h3>
          <div className={styles.orderItemsList}>
            {orderItems.map((item, index) => (
              <div key={index} className={styles.orderItem}>
                <span className={styles.orderItemName}>
                  {item.name} (Size: {item.size}) x{item.quantity}
                </span>
                <span className={styles.orderItemPrice}>
                  ${(item.price * item.quantity).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Explanation */}
      <div className={styles.statusExplanation}>
        <h3 className={styles.explanationTitle}>What this means:</h3>
        {orderStatus === 'paid' && (
          <p className={styles.explanationText}>
            Your order has been paid for and is being prepared for shipping.
            You&apos;ll receive a shipping confirmation email once your order
            ships.
          </p>
        )}
        {orderStatus === 'processing' && (
          <p className={styles.explanationText}>
            Your order is being processed and prepared for shipping. This
            usually takes 1-2 business days.
          </p>
        )}
        {orderStatus === 'shipped' && (
          <p className={styles.explanationText}>
            Your order has been shipped! You can track your package using the
            tracking number provided in your shipping email.
          </p>
        )}
        {orderStatus === 'delivered' && (
          <p className={styles.explanationText}>
            Your order has been delivered! Thank you for your purchase.
          </p>
        )}
      </div>
    </div>
  );
};

export default OrderStatus;
